import React, { ComponentRef, useCallback, useRef, useState } from 'react';

import { Button, theme, VStack } from '@sg/garnish';

import { GroupOrderIntro, type useGroupOrderingContent } from '@order/features-group-ordering';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderIntroModal = (props: Props) => {
  const { visible, content, onPrimaryCtaPress, onSecondaryCtaPress, onStartOrder } = props;

  // ─── Refs ─────────────────────────────────────────────────────────────

  const carouselRef = useRef<ComponentRef<typeof GroupOrderIntro.Carousel>>(null);

  // ─── State ────────────────────────────────────────────────────────────

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // ─── Derived ──────────────────────────────────────────────────────────

  const { introCards } = content;
  const isLastSlide = activeSlideIndex === introCards.length - 1;

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleNextPress = useCallback(() => {
    onPrimaryCtaPress();
    carouselRef.current?.goToNextSlide();
  }, [onPrimaryCtaPress]);

  const handleCancelPress = useCallback(() => {
    onSecondaryCtaPress();
  }, [onSecondaryCtaPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GroupOrderIntro.Modal visible={visible}>
      <GroupOrderIntro.Carousel ref={carouselRef} onActiveSlideIndexChange={setActiveSlideIndex}>
        {introCards.map((card, index) => (
          <GroupOrderIntro.Card
            key={card.fields.title + index}
            badge={card.fields.badge}
            title={card.fields.title}
            text={card.fields.text}
            image={card.fields.image}
          />
        ))}
      </GroupOrderIntro.Carousel>

      <GroupOrderIntro.Footer>
        <GroupOrderIntro.Dots total={introCards.length} active={activeSlideIndex} />

        <VStack gap={theme.spacing['2']}>
          {isLastSlide ? (
            <Button onPress={onStartOrder} size="large" palette="light">
              {content.introModalStartOrderButtonLabel}
            </Button>
          ) : (
            <Button onPress={handleNextPress} size="large" palette="light">
              {content.introModalNextButtonLabel}
            </Button>
          )}

          <Button onPress={handleCancelPress} size="large">
            {content.introModalCancelButtonLabel}
          </Button>
        </VStack>
      </GroupOrderIntro.Footer>
    </GroupOrderIntro.Modal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  content: GroupOrderingContent;
  onPrimaryCtaPress: () => void;
  onSecondaryCtaPress: () => void;
  onStartOrder: () => void;
};

type GroupOrderingContent = ReturnType<typeof useGroupOrderingContent>['mappedContent'];
